// @ts-nocheck
import { useLayoutEffect, useRef, useState } from 'react';
import useLazyRef from './useLazyRef';
/*
 * Hook for listening to size changes on elements on the DOM and return the bounding rect for that component.
 * It can either listen to one component, then id suggest using the ref provided by the hook or
 * it can listen to changes on the el(element) passed in the params and change the element its listening
 * dinamically.
 */
export default function useResizeObserver(el: Element | null = null) {
  const elementRef = useRef(null);
  const [rect, setRect] = useState(() =>
    elementRef.current
      ? elementRef.current.getBoundingClientRect()
      : {
          width: 0,
          height: 0,
          bottom: 0,
          top: 0,
          right: 0,
          left: 0,
          x: 0,
          y: 0,
        },
  );

  const resizeObserverRef = useLazyRef(
    () =>
      new ResizeObserver((entries) => {
        entries.forEach(({ target }) => setRect(target.getBoundingClientRect()));
      }),
  );

  useLayoutEffect(() => {
    // Update position of element on window resize
    const element = el || elementRef.current;
    // ☝ get element either from params or ref
    function handleWindowResize() {
      setRect(element.getBoundingClientRect());
    }
    if (element) window.addEventListener('resize', handleWindowResize);
    return () => {
      if (element) window.removeEventListener('resize', handleWindowResize);
    };
  }, [el]);

  useLayoutEffect(() => {
    // Update position of element on target element resize
    const resizeObserver = resizeObserverRef.current;
    // ☝ get element either from params or ref
    const element = el || elementRef.current;
    if (element) resizeObserver.observe(element);
    return () => {
      if (element) {
        resizeObserver.unobserve(element);
        resizeObserver.disconnect();
      }
    };
  }, [el, resizeObserverRef]);

  return [rect, elementRef];
}
